import { Alert } from '@neo/primitives/alert'
import { Button, ButtonWithLoader } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useNavigate } from 'router-v6'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { usePhoneDetails } from 'v2/pages/settings/security/queries'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { logger } from 'v2/utils/logger'
import { errorToast, infoToast, successToast } from 'v2/utils/toasts'
import { Currency } from '../billing.config'
import { resolveSelectedCardId } from '../components/card-selection'
import type { RazorpayMethod } from '../components/razorpay-method-picker'
import {
  getRazorpayMethodAvailability,
  resolveRazorpayMethod,
} from '../components/razorpay-methods'
import { useRechargeMutation } from '../mutations'
import {
  useAccountDetailsQuery,
  useBillingCardsQuery,
  useBillingRechargeDetailsQuery,
  useEnsureActiveRazorpayVbank,
  useRazorpayCreateOrderMutation,
  useRazorpayPayWithTokenMutation,
  useRazorpayVerifyPaymentMutation,
} from '../queries'
import { savePendingRazorpayPayment } from '../razorpay-pending-payment'
import { isUserCancelledPayment, submitRazorpayCustomCardPayment } from '../razorpay-sdk'
import {
  apiErrorMessage,
  buildPaymentCallbackUrl,
  getBankRailDisabledReason,
  getCardFeeLabel,
  getSortedRechargeChoices,
} from '../recharge-shared'
import {
  BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS,
  type BillingRefreshOptions,
  refreshBillingAfterPayment,
} from '../refresh-billing-queries'
import { runTrialUpgradeFollowUp, shouldRunTrialFollowUp } from '../trial-upgrade-followup'
import { CardState } from '../types'
import { buildUpgradePlanPatch } from '../upgrade-plan'
import { getAccountId } from '../utils'
import { RazorpayBankTransferDrawer } from './razorpay-bank-transfer-drawer'
import {
  getCardEntryBlocker,
  type RazorpayCustomCardFieldValues,
  shouldShowAddPhonePrompt,
} from './razorpay-card-entry'
import { RazorpayCustomAmountStep } from './razorpay-custom-amount-step'
import { RazorpayCustomCardFields } from './razorpay-custom-card-fields'
import { resolveRechargeCtaBlocker } from './recharge-cta'
import { useCashfreeNetbankingCheckout } from './use-cashfree-netbanking-checkout'
import { isRazorpaySavedCard } from './use-razorpay-standard-checkout'

// Razorpay CUSTOM Checkout (razorpay.js) — the one Razorpay checkout, and the
// entry point every INR surface opens. The card fields are OUR OWN inputs; the
// raw PAN/CVV are passed to razorpay.createPayment() (browser → Razorpay). This
// is PCI-DSS SAQ A-EP and Razorpay gates raw-card createPayment behind PCI
// certification, so on a non-PCI merchant it renders but returns payment.error.
// It won the evaluation against the hosted Standard variant, which is deleted;
// the Standard SDK survives only for saved-card charges (see
// use-razorpay-standard-checkout).
//
// Two steps, because the amount belongs to the first screen only:
//   'select' — amount chips, saved cards, Add Card.
//   'card'   — the raw card fields ALONE. Cancel/dismiss returns to 'select'
//              rather than closing the dialog, so the customer never loses the
//              amount they already picked.
//
// Saved cards are charged server-to-server, per gateway:
//   - Razorpay-tokenised card -> /v2/razorpay/pay-with-token (Native/S2S JSON API,
//     customer present; returns a 3DS URL to redirect to). Needs Razorpay's
//     Native/S2S API enabled on the merchant — a different enablement from the PCI
//     certification the raw-PAN path needs.
//   - Card from any other gateway (legacy Stripe/Cashfree tokens still on the
//     account) -> the legacy /recharge/:card_mapping_id charge, so multi-gateway
//     accounts keep working.
//
// Netbanking and bank transfer are offered here too (policy in
// components/razorpay-methods.ts; bank transfer is still flag-gated, only cards
// went GA), but only bank transfer is actually Razorpay's:
// it provisions/reuses the account's Smart Collect virtual account and shows it
// in the shared drawer. NETBANKING RUNS ON CASHFREE — Razorpay does not support
// it — via useCashfreeNetbankingCheckout, which is a full-page redirect out of
// this dialog.
interface RazorpayCustomRechargeModalProps {
  isOpen: boolean
  onClose: () => void
  restrictToMethod?: 'card'
}

// Not prefixed: /api registers no settings tree, so usePrefixedPath would 404 here.
const ADD_PHONE_PATH = '/profile/2fa'

type Step = 'select' | 'card'

const EMPTY_CARD_FIELDS: RazorpayCustomCardFieldValues = {
  cardNumber: '',
  cardName: '',
  expMonth: '',
  expYear: '',
  cvv: '',
  email: '',
  contact: '',
}

export const RazorpayCustomRechargeModal = ({
  isOpen,
  onClose,
  restrictToMethod,
}: RazorpayCustomRechargeModalProps) => {
  const accountId = getAccountId()
  const navigate = useNavigate()
  const { details } = useAuth()
  // isSuccess, not !isPending: a rejected query is not "settled, no phone".
  const { data: phoneDetailsData, isSuccess: isPhoneSettled } = usePhoneDetails()
  const { data: accountDetails } = useAccountDetailsQuery()
  const { isEnterprisePlan, isTrial } = useUserPlan()
  // Only for the trial plan poll below — refetch drives it, plan seeds the baseline.
  const { data: profileData, refetch: refetchProfile } = useProfileQuery()
  const { data: rechargeDetails } = useBillingRechargeDetailsQuery()
  const currency = rechargeDetails?.currency || Currency.INR
  const isAddCardIntent = restrictToMethod === 'card'
  // `isCashFreeTesting` is the existing internal-QA flag; it bypasses BOTH bank
  // rails' floors on both gateways so QA can verify at any amount.
  const { isCashFreeTesting } = useFeatureFlags()

  // Netbanking for every INR account, bank transfer for Enterprise INR, neither
  // on the add-card entry point.
  const { showNetbanking, showBankTransfer, availableMethods } = useMemo(
    () =>
      getRazorpayMethodAvailability({
        isAddCardIntent,
        currency,
        isEnterprisePlan,
      }),
    [isAddCardIntent, currency, isEnterprisePlan],
  )
  const [userSelectedMethod, setUserSelectedMethod] = useState<RazorpayMethod>('card')
  const showMethodPicker = !restrictToMethod && availableMethods.length > 1

  // Amount tiles from the account's backend recharge_choices — same source as
  // Cashfree so the options are identical across INR gateways.
  const sortedRechargeChoices = useMemo(
    () => getSortedRechargeChoices(rechargeDetails?.recharge_choices),
    [rechargeDetails?.recharge_choices],
  )

  const defaultSelectable = sortedRechargeChoices?.[0] ?? 0

  const [userSelectedAmount, setUserSelectedAmount] = useState<number | null>(null)
  const selectedAmount = userSelectedAmount ?? defaultSelectable

  // One reason per rail. Both floors are ₹10,000 today but for independent
  // reasons (see recharge-shared), so they are read separately.
  const netbankingDisabledReason = getBankRailDisabledReason(
    'netbanking',
    selectedAmount,
    currency,
    isCashFreeTesting,
  )
  const bankTransferDisabledReason = getBankRailDisabledReason(
    'bank_transfer',
    selectedAmount,
    currency,
    isCashFreeTesting,
  )
  // The reason for the rail the USER picked, which is what decides whether the
  // selection survives; card has no floor.
  const selectedRailDisabledReason =
    userSelectedMethod === 'netbanking'
      ? netbankingDisabledReason
      : userSelectedMethod === 'bank_transfer'
        ? bankTransferDisabledReason
        : undefined

  const method = resolveRazorpayMethod({
    restrictToMethod,
    userSelectedMethod,
    availableMethods,
    selectedRailDisabledReason,
  })
  const isNetbanking = method === 'netbanking'
  const isBankTransfer = method === 'bank_transfer'

  const [bankTransferOpen, setBankTransferOpen] = useState(false)
  const [vbankId, setVbankId] = useState('')
  const [isPreparingBankTransfer, setIsPreparingBankTransfer] = useState(false)
  const ensureActiveRazorpayVbank = useEnsureActiveRazorpayVbank()

  const [step, setStep] = useState<Step>('select')
  // Our own card inputs (this is the whole point of custom checkout).
  const [cardFields, setCardFields] = useState<RazorpayCustomCardFieldValues>(EMPTY_CARD_FIELDS)
  const setCardField = (field: keyof RazorpayCustomCardFieldValues, value: string) =>
    setCardFields((prev) => ({ ...prev, [field]: value }))
  const [processing, setProcessing] = useState(false)
  // Save-card consent, checked by default on BOTH entry points.
  //
  // This was raised in review (PR #3636): pre-ticking on Add Credits puts a card
  // on file for someone who came to top up, and RBI card-on-file guidance expects
  // explicit consent — so the safe engineering default would be on for Add New
  // Card and off for recharge. Answering it here rather than leaving the question
  // open: PRODUCT DECIDED pre-checked everywhere. Do not "fix" this back to
  // opt-in without taking it to product first.
  //
  // What keeps it defensible: the box is visible, unticking it genuinely charges
  // without storing (Razorpay only vaults when we ask — save_card on the order
  // plus save:1 on the payment), so consent is still the customer's to withdraw
  // before paying. On Add New Card the default is simply correct — that flow
  // exists to vault a card, and unticked it charges and stores nothing.
  const [saveCard, setSaveCard] = useState(true)

  // Razorpay-synced cards land in the shared cards table, so the existing
  // /account/card endpoint returns them (with card_mapping_id) too — along with
  // any older cards the account still has from Stripe/Cashfree. All of them are
  // offered; handlePaySavedCard routes each to the gateway that can charge it.
  const { data: cards } = useBillingCardsQuery()
  const activeCards = useMemo(
    () => (cards ?? []).filter((c) => c.state === CardState.ACTIVE),
    [cards],
  )
  const [selectedCardId, setSelectedCardId] = useState<number>(0)
  // Shared with the other checkout modals: the picker can delete the selected
  // card, so the effective id has to be derived, not stored.
  const effectiveSelectedCardId = useMemo(
    () => resolveSelectedCardId(activeCards, selectedCardId),
    [activeCards, selectedCardId],
  )
  const selectedCard = activeCards.find((c) => c.card_mapping_id === effectiveSelectedCardId)

  const queryClient = useQueryClient()
  const { mutateAsync: createOrder, isPending: creatingOrder } = useRazorpayCreateOrderMutation()
  const { mutateAsync: verifyPayment } = useRazorpayVerifyPaymentMutation()
  const { mutateAsync: payWithToken, isPending: payingWithToken } =
    useRazorpayPayWithTokenMutation()
  // Legacy server-side charge for saved cards that belong to another gateway.
  const { mutateAsync: recharge, isPending: legacyPayPending } = useRechargeMutation()

  // Saved cards are offered on the recharge flow only, and only for the card
  // method — the add-card entry exists to vault a NEW card, so it goes to step
  // 'card' with nothing to select.
  const showSavedCards = !isAddCardIntent && method === 'card' && activeCards.length > 0
  const payWithSavedCard = step === 'select' && showSavedCards && !!effectiveSelectedCardId

  // Prefill from what we actually have: email from the account's billing_email
  // (the billing address object has no email/phone), contact from the logged-in
  // user's 2FA/onboarding phone via usePhoneDetails (the auth session has no
  // phone). User can still edit; contact is required by createPayment.
  const prefillEmail =
    accountDetails?.account?.billing_email || accountDetails?.account?.email || details?.email || ''
  const prefillContact =
    phoneDetailsData?.data?.body?.verified_phone_number ||
    phoneDetailsData?.data?.body?.unverified_phone_number ||
    ''
  const email = cardFields.email || prefillEmail
  const contact = cardFields.contact || prefillContact
  // One home for the rule, beside the inputs that enforce it — the modal used to
  // accept a single-digit month because it only checked the field was non-empty.
  // One derivation for both the gate and the reason shown on the disabled button.
  // They must not be spelled separately: email and contact are prefilled and off
  // screen, so a generic "Enter card details" would name the wrong thing whenever
  // the account is the one missing data.
  const cardEntryBlocker = getCardEntryBlocker({ ...cardFields, email, contact })
  const cardComplete = cardEntryBlocker === undefined
  const showAddPhonePrompt = shouldShowAddPhonePrompt({
    isCardStep: step === 'card',
    isCardMethod: method === 'card',
    payWithSavedCard,
    contact,
    isPhoneSettled,
  })

  // The credit (and a newly-vaulted card) lands via the async payment.captured
  // webhook, AFTER verify returns — so this polls until the value actually moves
  // instead of invalidating once against a balance the backend hasn't updated yet.
  const refreshBillingQueries = (options?: BillingRefreshOptions) => {
    void refreshBillingAfterPayment(queryClient, accountId, options)
  }

  // A trial account's plan is flipped by the gateway's capture webhook (the order
  // carries upgrade_plan), which lands AFTER every handler below returns. The shared
  // follow-up polls the profile so the header stops saying "Free trial", and emits
  // PLAN_UPGRADED once it actually flips — that event has exactly one other emitter
  // (useChangePlanMutation), which this rail bypasses. See ../trial-upgrade-followup.
  const pollTrialPlan = () => {
    runTrialUpgradeFollowUp({
      isTrial,
      refetchProfile,
      currentPlan: profileData?.plan,
      amount: selectedAmount,
      currency,
      method,
    })
  }

  const resetAndClose = () => {
    setUserSelectedAmount(null)
    setCardFields(EMPTY_CARD_FIELDS)
    setSelectedCardId(0)
    // Back to the default, not to false — this reset runs on close, so leaving it
    // false would silently turn the box off for the next visit.
    setSaveCard(true)
    setUserSelectedMethod('card')
    setBankTransferOpen(false)
    setVbankId('')
    setStep('select')
    onClose()
  }

  // Netbanking is Cashfree's — Razorpay does not support it. Same launcher the
  // Standard modal and the Payment Modes tile use.
  const { launchNetbanking, isLaunchingNetbanking } = useCashfreeNetbankingCheckout()

  // Split deliberately: these are network mutations (or the hosted modal, which
  // always reports back via handler/ondismiss) that settle, so blocking close
  // while they run is safe. `processing` is razorpay.js's event-driven phase,
  // which can NEVER settle (a blocked or dismissed 3DS popup fires neither
  // payment.success nor payment.error) — so it must not be allowed to block
  // dismissal. See handleClose.
  const mutationPending =
    creatingOrder ||
    payingWithToken ||
    legacyPayPending ||
    // Holds the CTA while the Cashfree order is created and the tab navigates
    // away — without it the button is live during the redirect.
    isLaunchingNetbanking ||
    isPreparingBankTransfer
  const isPending = mutationPending || processing

  // Bank transfer: resolve the account's Smart Collect virtual account, then
  // open the read-only details drawer. payment-service owns the vbank_id and
  // returns the existing VA or provisions one.
  const handleBankTransfer = async () => {
    if (isPending || !selectedAmount) return
    setIsPreparingBankTransfer(true)
    try {
      const id = await ensureActiveRazorpayVbank()
      setVbankId(id)
      setBankTransferOpen(true)
    } catch (error) {
      logger.error('RazorpayCustomRechargeModal: ensure vbank failed', { error })
      errorToast({
        title: 'Could not load bank transfer details',
        description: apiErrorMessage(error, 'Please try again in some time.'),
      })
    } finally {
      setIsPreparingBankTransfer(false)
    }
  }

  // Dismissing the card step goes BACK to the amount/saved-card step instead of
  // closing, so the amount already chosen (and the saved-card list) is still there.
  // Applies to both entry points — Billing overview and Payment Methods.
  const handleClose = () => {
    // Never interrupt an in-flight mutation — it will settle on its own.
    if (mutationPending) return
    // Escape hatch for the razorpay.js phase. Without this, a 3DS popup that was
    // blocked or closed leaves `processing` true forever: Cancel disabled, Esc and
    // click-outside swallowed, and a payment already created at Razorpay — the
    // only way out is a page reload. Bail out and tell the customer the truth:
    // we cannot confirm the outcome from here, the webhook owns it.
    if (processing) {
      setProcessing(false)
      infoToast({
        title: 'Payment not confirmed',
        description:
          'We could not confirm this payment. If you completed it, your balance will update shortly — check Transactions before retrying.',
      })
      resetAndClose()
      return
    }
    if (step === 'card') {
      setCardFields(EMPTY_CARD_FIELDS)
      setStep('select')
      return
    }
    resetAndClose()
  }

  // Charge a saved card. A Razorpay-tokenised card goes through
  // /v2/razorpay/pay-with-token; anything else keeps the legacy /recharge path so
  // cards vaulted with a previous gateway remain chargeable.
  const handlePaySavedCard = async () => {
    if (isPending || !selectedAmount || !effectiveSelectedCardId) return
    if (!isRazorpaySavedCard(selectedCard)) return handlePayLegacySavedCard()
    try {
      const res = await payWithToken({
        account_id: accountId,
        amount: selectedAmount,
        card_mapping_id: effectiveSelectedCardId,
        product_type: 'plivo',
        // A 3DS saved-card charge navigates the whole tab to Razorpay's OTP page,
        // so this is the ONLY route back — there is no in-page handler left to
        // fire (unlike the new-card flow, where razorpay.js keeps 3DS in the tab
        // with redirect:false and reports the outcome as an event). Omitting it
        // stranded the customer on Razorpay after a SUCCESSFUL payment, and
        // pay-with-token is the one Razorpay path with no server-side fallback.
        //
        // Razorpay returns by auto-submitting a form, so the console receives a
        // POST it cannot read — which is fine: /payment/callback resolves the
        // outcome from the stash written below plus a live status fetch, and
        // reads `method` from the QUERY STRING, which survives the POST.
        callback_url: buildPaymentCallbackUrl('razorpay'),
        // Same contract as the new-card order below: pay-with-token creates its own
        // Razorpay order carrying these Notes, so order.paid does the plan flip. Not
        // trial-reachable today (saved-card paths need !isAddCardIntent); kept so
        // every money path in this modal behaves alike.
        ...buildUpgradePlanPatch(isTrial),
      })
      if (!res.success) {
        errorToast({ title: 'Payment Failed', description: res.message || 'Please try again.' })
        return
      }
      // Hand off to Razorpay's OTP page. Don't refresh billing queries or close
      // with a success toast — nothing has been credited yet.
      if (res.requires_action && res.next_action_url) {
        // Written BEFORE the navigation: once this tab leaves, res.payment_id is
        // the only handle on this charge, and the return route has no other way
        // to learn whether it succeeded (Razorpay's answer arrives as a POST body
        // the SPA cannot read). Storage failures are swallowed inside the helper,
        // so the return route falls back to its outcome-neutral branch rather
        // than this throwing mid-payment.
        savePendingRazorpayPayment({
          paymentId: res.payment_id,
          orderId: res.order_id,
          accountId,
          amount: selectedAmount,
          startedAt: Date.now(),
        })
        window.location.href = res.next_action_url
        return
      }
      // Submitted with no 3DS step. The webhook still owns the terminal state, so
      // report "processing" rather than success and let the refresh reconcile —
      // on the longer window, since the gateway has told us it isn't done yet.
      refreshBillingQueries({ maxAttempts: BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS })
      pollTrialPlan()
      infoToast({
        title: 'Payment processing',
        description: 'Your payment is being confirmed and will reflect shortly.',
      })
      resetAndClose()
    } catch (error) {
      logger.error('RazorpayCustomRechargeModal: pay-with-token failed', { error })
      errorToast({
        title: 'Payment Failed',
        description: apiErrorMessage(error, 'We could not charge this card. Please try another.'),
      })
    }
  }

  // Server-side charge by card_mapping_id — routes to the card's own gateway.
  const handlePayLegacySavedCard = async () => {
    try {
      const res = await recharge({
        account_id: accountId,
        card_mapping_id: effectiveSelectedCardId,
        amount: String(selectedAmount),
        // /recharge accepts upgrade_plan too (structs.RechargeRequest -> Stripe charge
        // metadata). Not trial-reachable today: the saved-card paths need
        // !isAddCardIntent and a trial only opens this modal via Add Card. Kept for
        // intra-module consistency — every money path here behaves alike, rather than
        // new-card carrying the plan while saved-card silently does not.
        ...buildUpgradePlanPatch(isTrial),
      })
      // A 3DS redirect isn't a completed recharge — hand off to the bank page.
      // Refresh only AFTER this check: starting a poll and then navigating away
      // in the same tick throws the poll away with the page.
      if (res.redirection_url) {
        window.location.href = res.redirection_url
        return
      }
      refreshBillingQueries()
      if (res.success) {
        pollTrialPlan()
        successToast({ title: 'Success', description: 'Recharge successful' })
      } else {
        errorToast({ title: 'Payment Failed', description: res.message || 'Please try again.' })
      }
      resetAndClose()
    } catch (error) {
      logger.error('RazorpayCustomRechargeModal: legacy saved-card recharge failed', { error })
      errorToast({
        title: 'Payment Failed',
        description: apiErrorMessage(
          error,
          'We could not charge this card. Please try another card.',
        ),
      })
    }
  }

  const handlePayNewCard = async () => {
    if (isPending || !selectedAmount || !cardComplete) return
    let order: Awaited<ReturnType<typeof createOrder>>
    try {
      order = await createOrder({
        account_id: accountId,
        amount: selectedAmount,
        currency: 'INR',
        product_type: 'plivo',
        payment_method: 'card',
        // false => backend skips Razorpay customer provisioning entirely, and the
        // payment still goes through (verified in usecases/razorpay_payment.go).
        save_card: saveCard,
        // Send the customer's name/email/phone so the Razorpay customer is
        // created with real details instead of the backend's synthetic fallback.
        customer_name: cardFields.cardName || details?.name,
        customer_email: email,
        customer_phone: contact,
        // Trial account: this is the LIVE trial rail — AddCardModal routes an INR
        // org here, and adding a card is a real charge, so without this the customer
        // is charged and never upgraded. Razorpay stashes it in the order Notes and
        // order.paid upgrades the plan after crediting. See ../upgrade-plan.
        ...buildUpgradePlanPatch(isTrial),
      })
    } catch (error) {
      logger.error('RazorpayCustomRechargeModal: order create failed', { error })
      errorToast({
        title: 'Payment Failed',
        description: apiErrorMessage(error, 'We faced an error. Please try again.'),
      })
      return
    }
    if (!order.razorpay_order_id || !order.key_id) {
      errorToast({
        title: 'Card payments are not configured',
        description: 'Please contact support.',
      })
      return
    }
    // A save_card order can return 200 without a customer id (best-effort backend
    // provisioning). On the add-card journey a silent no-save defeats the purpose,
    // so bail — but only when the customer actually asked to save: with the box
    // unchecked the backend never provisions a customer and its absence is
    // expected, not a failure. A plain recharge proceeds either way and simply
    // doesn't advertise a save that won't happen.
    if (isAddCardIntent && saveCard && !order.razorpay_customer_id) {
      logger.error('RazorpayCustomRechargeModal: save_card order missing razorpay_customer_id')
      errorToast({
        title: 'Could not add card',
        description: 'We could not set up card saving right now. Please try again.',
      })
      return
    }

    // Go non-modal (modal={!processing}) so razorpay.js's 3DS/OTP popup — appended
    // to <body>, outside this dialog — isn't trapped in Radix's inert region.
    setProcessing(true)
    try {
      await submitRazorpayCustomCardPayment(
        order.key_id,
        {
          amount: order.amount_paise,
          currency: order.currency || 'INR',
          order_id: order.razorpay_order_id,
          email,
          contact,
          method: 'card',
          card: {
            number: cardFields.cardNumber.replace(/\s+/g, ''),
            name: cardFields.cardName || 'Cardholder',
            expiry_month: cardFields.expMonth,
            expiry_year: cardFields.expYear,
            cvv: cardFields.cvv,
          },
          // Only vault when the customer asked AND the backend gave us a customer
          // to vault against; otherwise this is a one-off charge.
          customer_id: saveCard ? order.razorpay_customer_id : undefined,
          save: saveCard && order.razorpay_customer_id ? 1 : 0,
        },
        {
          onSuccess: async (resp) => {
            try {
              const v = await verifyPayment({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              })
              // Only track the cards list when the customer actually asked to
              // save — otherwise we'd poll for a card that is never coming.
              refreshBillingQueries({
                targets: saveCard
                  ? ['balance', 'cards', 'transactions']
                  : ['balance', 'transactions'],
                ...(v.captured === false
                  ? { maxAttempts: BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS }
                  : {}),
              })
              if (!v.verified) {
                errorToast({ title: 'Payment Failed', description: 'Please try again.' })
              } else {
                // Every VERIFIED payment, captured or not. The plan flip rides on the
                // capture webhook, so the pending-capture branch is the one with an
                // upgrade still coming — see shouldRunTrialFollowUp.
                if (shouldRunTrialFollowUp(v)) pollTrialPlan()
                if (v.captured === false) {
                  infoToast({
                    title: 'Payment processing',
                    description: 'Your payment is being confirmed and will reflect shortly.',
                  })
                } else {
                  successToast({ title: 'Success', description: 'Payment successful' })
                }
              }
            } catch (error) {
              logger.error('RazorpayCustomRechargeModal: verify failed', { error })
              errorToast({
                title: 'Payment Failed',
                description: apiErrorMessage(error, 'Please try again.'),
              })
            } finally {
              setProcessing(false)
              resetAndClose()
            }
          },
          onError: (payload) => {
            setProcessing(false)
            // Backing out of 3DS is not a failure. It arrives on this same channel
            // because redirect:false never navigates, so it has to be separated
            // here — otherwise someone who chose to cancel is told their card was
            // refused. Not logged at error level either: nothing went wrong.
            //
            // The modal stays on the card step with the entered details intact, so
            // retrying is one click; wiping the form would punish the cancel.
            if (isUserCancelledPayment(payload)) {
              logger.info('RazorpayCustomRechargeModal: payment cancelled by user')
              infoToast({
                title: 'Payment cancelled',
                description: 'You cancelled the payment. Your card details are still here.',
              })
              return
            }
            logger.error('RazorpayCustomRechargeModal: payment.error', { payload })
            // Surface the REAL Razorpay error rather than assuming a cause.
            const description =
              (payload as { error?: { description?: string } })?.error?.description ||
              'Card payment could not be completed. Please try again.'
            errorToast({ title: 'Payment Failed', description })
          },
        },
      )
    } catch (error) {
      setProcessing(false)
      logger.error('RazorpayCustomRechargeModal: createPayment failed', { error })
      errorToast({ title: 'Payment Failed', description: 'Please try again.' })
    }
  }

  const ctaDisabledReason = resolveRechargeCtaBlocker({
    selectedAmount,
    method,
    netbankingDisabledReason,
    bankTransferDisabledReason,
    cardBlocker: step === 'card' ? cardEntryBlocker : undefined,
  })

  // "Add a new card" — the same words as the affordance that gets here, so the
  // dialog confirms what was just clicked rather than renaming it on arrival.
  const dialogTitle = (() => {
    if (step === 'card') return 'Add a new card'
    return isAddCardIntent ? 'Add a new card' : 'Add Credits'
  })()
  const ctaLabel = (() => {
    // "Recharge" on the card step too, including the Add-New-Card entry point:
    // adding a card here IS a recharge that vaults the card (the step's own note
    // says a minimum payment is required), so naming the card would hide the
    // charge the customer is about to authorise.
    if (step === 'card') return 'Recharge'
    if (isBankTransfer) return 'View Details'
    if (isNetbanking) return 'Pay'
    // "Recharge" names what the money buys; "Pay" could be any of the three rails.
    if (payWithSavedCard) return 'Recharge'
    return 'Continue'
  })()

  const handleCta = () => {
    if (step === 'card') return handlePayNewCard()
    if (isBankTransfer) return handleBankTransfer()
    if (isNetbanking)
      return launchNetbanking({
        amount: selectedAmount,
        source: 'razorpay-custom-recharge-modal',
        isTrial,
      })
    if (payWithSavedCard) return handlePaySavedCard()
    // No saved card to charge — collect a new one.
    return setStep('card')
  }

  return (
    <>
      {/* modal={!processing}: non-modal while razorpay.js drives the
          3DS popup OR the hosted netbanking checkout is open, so their iframes —
          both appended to <body> — are focusable/clickable rather than trapped in
          Radix's inert region (same reason as the Standard modal). The dialog is
          also suppressed while the bank-transfer drawer is up. */}
      <Dialog
        open={isOpen && !bankTransferOpen}
        modal={!processing}
        onOpenChange={(open) => !open && handleClose()}
      >
        {/* 512px (`max-w-lg`), the Add Card width — at 460px the labelled card
            fields sat in a narrow column. Applied to BOTH steps, not just the card
            one, so the dialog doesn't resize as the customer moves between them.
            No corner ✕: Cancel/Back is in the footer, so the dialog had two
            controls doing the same job in two places. Esc and a click outside
            still dismiss it (Radix defaults, left on). */}
        <DialogContent showCloseButton={false} className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {showAddPhonePrompt && (
              <div data-testid="alert-add-phone">
                <Alert
                  variant="info"
                  content={
                    <>
                      Add a phone number to your profile to pay by card.
                      {/* Add-card has no netbanking tile to switch to. */}
                      {showNetbanking && ' Netbanking works without one.'}{' '}
                      <button
                        type="button"
                        className="underline text-p-600"
                        data-testid="button-add-phone"
                        onClick={() => {
                          resetAndClose()
                          navigate(ADD_PHONE_PATH)
                        }}
                      >
                        Add phone number
                      </button>
                    </>
                  }
                />
              </div>
            )}
            {step === 'select' ? (
              <RazorpayCustomAmountStep
                currency={currency}
                choices={sortedRechargeChoices || []}
                selectedAmount={selectedAmount}
                onSelectAmount={setUserSelectedAmount}
                isPending={isPending}
                isAddCardIntent={isAddCardIntent}
                gatewayFeeRate={rechargeDetails?.gateway_fee_rate}
                cardFeeLabel={getCardFeeLabel(rechargeDetails?.gateway_fee_rate)}
                showSavedCards={showSavedCards}
                activeCards={activeCards}
                selectedCardId={effectiveSelectedCardId}
                onCardChange={setSelectedCardId}
                onAddCard={() => setStep('card')}
                method={method}
                methodPicker={
                  showMethodPicker
                    ? {
                        onSelectMethod: setUserSelectedMethod,
                        showNetbanking,
                        showBankTransfer,
                        netbankingDisabledReason,
                        bankTransferDisabledReason,
                      }
                    : undefined
                }
              />
            ) : (
              /* Card step renders the inputs ALONE — the amount was chosen on the
                 previous screen and Cancel returns there. */
              <RazorpayCustomCardFields
                values={{ ...cardFields, email, contact }}
                onChange={setCardField}
                disabled={isPending}
                saveCard={saveCard}
                onSaveCardChange={setSaveCard}
              />
            )}
          </div>

          {/* Both actions right, Cancel immediately left of the primary — the
              footer's own default grouping, and the pairing the eye reads as "this
              or that". Pushed to opposite edges, Cancel read as an unrelated
              control that happened to share the row. */}
          <DialogFooter className="flex flex-row justify-end gap-2 sm:space-x-0">
            {/* "Cancel" on both steps. Note the card step's handler still steps
                BACK to the amount screen rather than dismissing the dialog (see
                handleClose) — deliberate, so the customer doesn't lose the amount
                they picked, but it does mean the label and the action differ on
                that step. */}
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={mutationPending}
              data-testid="razorpay-custom-cancel-btn"
            >
              Cancel
            </Button>
            <ButtonWithLoader
              type="button"
              disabled={isPending || ctaDisabledReason !== undefined}
              disabledReason={ctaDisabledReason}
              loading={isPending}
              onClick={handleCta}
              data-testid="razorpay-custom-pay-btn"
            >
              {ctaLabel}
            </ButtonWithLoader>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RazorpayBankTransferDrawer open={bankTransferOpen} vbankId={vbankId} onClose={handleClose} />
    </>
  )
}
